import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import "./CopyMessage.css";
import {format} from 'timeago.js'
import ProfileSuggestions from './Suggestions';
import GroupVoteCard from './GroupVoteCard';
import InviteCard from './InviteCard';
import BotText from './BotText';
import { QUICK_REPLY_EVENT } from './Message';
import ChatSliderWidget, { SLIDER_WIDGET_PREFIX } from './ChatSliderWidget';
import ChatLocationWidget, { LOCATION_WIDGET_PREFIX } from './ChatLocationWidget';
import ChatProfileWidget, { PROFILE_WIDGET_PREFIX } from './ChatProfileWidget';
import WeeklyMatchCountdown from '../WeeklyMatchCountdown';

// The live round countdown (countdown-mode communities): a bot message whose
// text is this prefix + JSON — must match triggerWelcomeMessage.js.
const COUNTDOWN_WIDGET_PREFIX = '__COUNTDOWN_WIDGET__';

const parseGroupInvite = (text) => {
  if (!text || !text.startsWith('{"type":"group_invite"')) return null;
  try {
    const parsed = JSON.parse(text);
    return parsed.type === 'group_invite' ? parsed : null;
  } catch (_) { return null; }
};

const Message = ({message, own, otherUserPic, reciverUserPic, onToChatProfileForCSS, onToSuggestionProfileForCSS, isGroupConversation, showQuickReplies = false}) => {

  // The community bot's messages are shown like /meet's: plain text, no
  // bubble — and one that's only just arrived fades in a few words at a time.
  const isBot = !own && !isGroupConversation && !!message.communityId && String(message.sender) === String(message.communityId);
  const [reveal] = useState(() => isBot && Date.now() - new Date(message.createdAt).getTime() < 20000);

  const text = typeof message.text === 'string' ? message.text : '';
  const groupInvite = parseGroupInvite(text);
  const sanitizedHTML = groupInvite ? '' : DOMPurify.sanitize(text);

  // Tap-to-reply buttons under the bot's latest message (sent as if typed).
  const quickReplies = showQuickReplies && !own && message.quickReplies?.length > 0 && (
    <div className="messageQuickReplies">
      {message.quickReplies.map((r) => (
        <button
          key={r.id}
          type="button"
          className="messageQuickReply"
          onClick={() => window.dispatchEvent(new CustomEvent(QUICK_REPLY_EVENT, { detail: { text: r.label } }))}
        >
          {r.label}
        </button>
      ))}
    </div>
  );

  // Widgets the bot sends as coded text: the round countdown, a slider from
  // their profile (e.g. how far away people can be) and the box for their town.
  if (text.startsWith(COUNTDOWN_WIDGET_PREFIX)) {
    let widgetData = {};
    try { widgetData = JSON.parse(text.slice(COUNTDOWN_WIDGET_PREFIX.length)); } catch { /* ignore malformed payload */ }
    if (typeof widgetData.dayOfWeek !== 'number' || typeof widgetData.hour !== 'number') return null;
    return (
      <div className="message-countdown-widget">
        <WeeklyMatchCountdown dayOfWeek={widgetData.dayOfWeek} hour={widgetData.hour} label={widgetData.label} light />
      </div>
    );
  }
  if (text.startsWith(SLIDER_WIDGET_PREFIX)) {
    let sliderData = {};
    try { sliderData = JSON.parse(text.slice(SLIDER_WIDGET_PREFIX.length)); } catch { /* ignore malformed payload */ }
    if (typeof sliderData.field !== 'string') return null;
    return (
      <>
        <ChatSliderWidget communityId={message.communityId} field={sliderData.field} />
        {quickReplies}
      </>
    );
  }
  if (text.startsWith(PROFILE_WIDGET_PREFIX)) {
    let profileData = {};
    try { profileData = JSON.parse(text.slice(PROFILE_WIDGET_PREFIX.length)); } catch { /* ignore malformed payload */ }
    return <ChatProfileWidget communityId={message.communityId} sections={Array.isArray(profileData.sections) ? profileData.sections : []} />;
  }
  if (text.startsWith(LOCATION_WIDGET_PREFIX)) {
    return (
      <>
        <ChatLocationWidget />
        {quickReplies}
      </>
    );
  }

  return (
    <div className={`message ${own ? "own" : ""}${isBot ? " message--bot" : ""}`}>
      <div className="messageTop">
        {own ? (
          <img
            className="messageImg"
            src={reciverUserPic?.[0] || 'https://i.pinimg.com/736x/dd/8b/a9/dd8ba98ba0b06489ac96f76b74fe7fc6.jpg'}
            alt=""
            onClick={onToChatProfileForCSS}
          />
        ) : (
          <img
            className="messageImg"
            src={otherUserPic?.[0] || 'https://i.pinimg.com/736x/dd/8b/a9/dd8ba98ba0b06489ac96f76b74fe7fc6.jpg'}
            alt=""
            onClick={onToChatProfileForCSS}
          />
        )}
        {!groupInvite && (isBot
          ? <div className="messageText messageText--bot"><BotText html={text} reveal={reveal} /></div>
          : <p className="messageText" dangerouslySetInnerHTML={{ __html: sanitizedHTML }}></p>
        )}
      </div>
      <div>
        {groupInvite ? (
          <InviteCard invite={groupInvite} />
        ) : message?.suggestions?.length > 0 && isGroupConversation ? (
          <GroupVoteCard
            key={`gvc-${message._id || message.messageId}`}
            userIds={message.suggestions}
            communityId={message.communityId}
            conversationId={message.conversationId}
          />
        ) : message?.suggestions?.length > 0 ? (
          <ProfileSuggestions
            key={`suggestions-${message._id || message.messageId}-${JSON.stringify(message.suggestions?.sort())}`}
            userIds={message.suggestions}
            communityId={message.communityId}
            communityPic={otherUserPic}
            conversationId={message.conversationId}
            onSuggestionProfileClick={onToSuggestionProfileForCSS}
          />
        ) : null}
      </div>
      {quickReplies}
      <div>
        {message.leaveConvoAfter && <div className="leftConversation"> {message.communityName} left the conversation </div>}
      </div>
      <div className="messageBottom">{format(message.createdAt)}</div>
    </div>
  );

}

export default Message;    





// return (
//   <div className={own ? "message own" : "message"}>
//       <div className="messageTop">
//           <img 
//               className="messageImg"
//               src= {own ? "https://static.vecteezy.com/system/resources/previews/034/222/551/non_2x/bearded-black-man-winking-smiling-2d-avatar-illustration-african-american-adult-sparkling-cartoon-character-face-portrait-friendly-flirty-guy-flat-color-user-profile-image-isolated-on-white-vector.jpg" : otherUserPic } 
//               alt=""
//           />
// {/* Use dangerouslySetInnerHTML to render the HTML content */}
// <div className="messageText" dangerouslySetInnerHTML={{ __html: message.text }}></div>
//       </div>
//       <div> 
//         {message.suggestions.length > 0  ?  <ProfileSuggestions userIds={message.suggestions} communityId={message.communityId} communityPic={otherUserPic} conversationId={message.conversationId}/>  : null /* <label> NO! suggestions </label> */}
//       </div>
//       <div className="messageBottom">{format(message.createdAt)}</div>
//   </div>
// ); 
// }




// import React from 'react';
// import "./Message.css";
// import {format} from 'timeago.js'

// const Message = ({message, own}) => {
//   return (
//     <div className={own ? "message own" : "message"}>
//         <div className="messageTop">
//             <img 
//                 className="messageImg"
//                 src="https://static.vecteezy.com/system/resources/previews/034/222/551/non_2x/bearded-black-man-winking-smiling-2d-avatar-illustration-african-american-adult-sparkling-cartoon-character-face-portrait-friendly-flirty-guy-flat-color-user-profile-image-isolated-on-white-vector.jpg" 
//                 alt=""
//             />
//             <p className="messageText">{message.text}</p>
//         </div>
//         <div className="messageBottom">{format(message.createdAt)}</div>
//     </div>
//   ); 
// }

// export default Message;    


//PROFILE SUGGESTIONS: 

// import React, { useState } from 'react';
// import "./Message.css";

// // Assuming you have a list of profiles like this
// const profiles = [
//   {
//     id: 1,
//     name: "John Doe",
//     bio: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
//     imageUrl: "https://example.com/john.jpg"
//   },
//   {
//     id: 2,
//     name: "Jane Doe",
//     bio: "Pellentesque habitant morbi tristique senectus et netus.",
//     imageUrl: "https://example.com/jane.jpg"
//   },
//   {
//     id: 3,
//     name: "Jim Doe",
//     bio: "Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
//     imageUrl: "https://example.com/jim.jpg"
//   }
// ];

// const Message = () => {
//   const [selectedProfiles, setSelectedProfiles] = useState([]);

//   const handleCheckboxChange = (profileId) => {
//     setSelectedProfiles(prev => {
//       if (prev.includes(profileId)) {
//         return prev.filter(id => id !== profileId);
//       } else {
//         return [...prev, profileId];
//       }
//     });
//   };

//   const handleSubmit = (event) => {
//     event.preventDefault();
//     console.log("Selected Profiles: ", selectedProfiles);
//     // Here you can do whatever you need with the selected profiles
//   };

//   return (
//     <form onSubmit={handleSubmit}>
//       {profiles.map(profile => (
//         <div key={profile.id} className="profile">
//           <img className="profileImg" src={profile.imageUrl} alt={profile.name} />
//           <p className="profileBio">{profile.bio}</p>
//           <label>
//             <input  
//               type="checkbox"
//               checked={selectedProfiles.includes(profile.id)}
//               onChange={() => handleCheckboxChange(profile.id)}
//             />
//             Connect with this person?
//           </label>
//         </div>
//       ))}
//       <button type="submit">Submit</button>
//     </form>
//   );
// }

// export default Message;